import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import { differenceInCalendarDays } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import {
  IS_PRO,
  SAMPLE_QUANTITY_CAP,
} from 'gatsby-theme-hem-store/src/constants';
import { useCustomer } from 'gatsby-theme-hem-store/src/hooks/useCustomer';
import { useIsSample } from 'gatsby-theme-hem-store/src/hooks/useIsSample';
import { useShopifyStore } from 'gatsby-theme-hem-store/src/hooks/useShopifyStore';
import { Button, LoadingIndicator, Select, transition } from 'hem-ui';
import isFunction from 'lodash/isFunction';
import React, { useEffect, useRef, useState } from 'react';

import { PanelEnum, usePanelStore } from '@/components/Panels/Panels';
import { StoreStatus } from '@/context/cart-context-types';
import { Inventory, Status } from '@/hooks/useInventory';
import { sendGtmProductAddToCartEvent } from '@/utils/sendGtmEvents';

export enum State {
  Idle,
  AddingToCart,
  AddedToCartSuccess,
}

type Props = {
  inventoryQuantity: number;
  inventoryData: {
    status: Status;
    error: unknown;
    data?: Inventory;
  };
  price: number;
  compareAtPrice: number;
  productName: string;
  sku: string;
  slug: string;
  variantName: string;
  shopifyVariantId: string;
  onAddToCart?: () => void;
  mtoMoq?: number;
};

export const AddToCart: React.FC<Props> = ({
  inventoryQuantity,
  inventoryData,
  price,
  compareAtPrice,
  productName,
  sku,
  slug,
  variantName,
  shopifyVariantId,
  onAddToCart,
  mtoMoq,
}): JSX.Element => {
  const defaultAddQty = mtoMoq && typeof mtoMoq === 'number' ? mtoMoq : 1;
  const { cart, status, addLineItems, updateLineItems } = useShopifyStore();
  const customer = useCustomer((state) => state.customer);
  const [state, setState] = useState(State.Idle);
  const prevState = useRef();
  const [addQuantity, setaddQuantity] = useState(defaultAddQty);
  const { setCurrentlyOpenPanel } = usePanelStore();
  const lineItems = cart?.lines?.nodes || [];

  const { isSample } = useIsSample(slug);

  const currentlyInCart =
    lineItems.find((lineItem) => lineItem.merchandise.sku === sku)?.quantity ??
    0;

  function handleQuantityChange(event: React.ChangeEvent<HTMLSelectElement>) {
    setaddQuantity(parseInt(event.target.value));
  }

  useEffect(() => {
    // @ts-expect-error to keep track of previous state
    prevState.current = state;
  }, [state]);

  useEffect(() => {
    if (status === StoreStatus.ADDING || status === StoreStatus.UPDATING) {
      setState(State.AddingToCart);
    }

    if (
      status === StoreStatus.IDLE &&
      prevState.current === State.AddingToCart
    ) {
      setState(State.AddedToCartSuccess);

      setaddQuantity(defaultAddQty);
      setCurrentlyOpenPanel(PanelEnum.Cart);

      setTimeout(() => {
        setState(State.Idle);
      }, 2000);
    }
  }, [defaultAddQty, setCurrentlyOpenPanel, status]);

  async function addToCart() {
    if (isFunction(onAddToCart)) {
      onAddToCart();
    }

    // if we have variant in cart, we need up update it (using
    // the checkout line item id), rather than adding a new line item
    const lineItem = lineItems.find(
      (item) => item.merchandise.id === shopifyVariantId,
    );

    if (lineItem !== undefined) {
      updateLineItems({
        lines: [
          {
            id: lineItem.id,
            quantity: lineItem.quantity + addQuantity,
          },
        ],
      });
    } else {
      addLineItems({
        lines: [
          {
            merchandiseId: shopifyVariantId,
            quantity: addQuantity,
          },
        ],
      });
    }

    sendGtmProductAddToCartEvent({
      sku: sku,
      price: price.toString(),
      originalPrice: (compareAtPrice || price).toString(),
      variantName,
      productName,
      slug,
      quantity: addQuantity,
    });
  }

  if (IS_PRO && !customer) {
    return null;
  }

  // limit inventoryQuantity to SAMPLE_QUANTITY_CAP if is sample
  const maxQuantity = isSample
    ? Math.min(inventoryQuantity, SAMPLE_QUANTITY_CAP)
    : inventoryQuantity;

  let count =
    mtoMoq && typeof mtoMoq === 'number' ? mtoMoq - 1 : 0 - currentlyInCart;

  return (
    <div className="mb-2 grid grid-cols-[minmax(80px,1fr)_4fr] gap-2 md:gap-4">
      <Select
        onChange={handleQuantityChange}
        disabled={maxQuantity === currentlyInCart}
        title="Quantity"
        defaultValue={1}
      >
        {inventoryData?.status === 'success' &&
          inventoryData.data.map((inventory) => {
            const options = React.Children.toArray(
              Array.from({ length: inventory.quantity }, () => {
                count++;
                if (count < 1 || count > maxQuantity) return null;
                return <option value={count}>{count}</option>;
              }).filter(Boolean),
            );

            if (options.length === 0) return null;

            const date = new Date(inventory.date);
            const now = new Date();
            const daysToShipment = differenceInCalendarDays(date, now);

            const headline = `Ships within ${
              daysToShipment > 7
                ? `${Math.ceil(daysToShipment / 7)} weeks`
                : '1-7 days'
            }`;

            return (
              <optgroup key={inventory.date} label={headline}>
                {options}
              </optgroup>
            );
          })}

        {inventoryData?.status !== 'success' &&
          Array.from({ length: maxQuantity - currentlyInCart }, (_, num) => {
            const value =
              mtoMoq && typeof mtoMoq === 'number' ? mtoMoq + num : num + 1;
            return (
              <option key={num} value={value}>
                {value}
              </option>
            );
          })}
      </Select>
      <Button
        onClick={addToCart}
        disabled={
          maxQuantity === currentlyInCart ||
          state === State.AddingToCart ||
          state === State.AddedToCartSuccess
        }
        color={
          state === State.AddedToCartSuccess
            ? 'green'
            : maxQuantity === currentlyInCart
              ? 'disabled'
              : 'blue'
        }
        data-selector="Add to cart button"
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            className="flex items-center justify-center gap-2"
            initial={{ translateY: -2, opacity: 0 }}
            animate={{ translateY: 0, opacity: 1 }}
            exit={{ translateY: 2, opacity: 0 }}
            key={state}
            {...transition}
          >
            {
              {
                [State.AddingToCart]: (
                  <>
                    <LoadingIndicator />
                    Adding to {IS_PRO ? 'quote' : 'shopping bag'}…
                  </>
                ),
                [State.AddedToCartSuccess]: (
                  <>
                    <CheckOutlinedIcon
                      fontSize="small"
                      style={{ fill: 'var(--color-white)' }}
                    />
                    Added to {IS_PRO ? 'quote' : 'shopping bag'}!
                  </>
                ),
                [State.Idle]: <>Add to {IS_PRO ? 'quote' : 'shopping bag'}</>,
              }[state]
            }
          </motion.div>
        </AnimatePresence>
      </Button>
    </div>
  );
};
